// src/contexts/AgentContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useApi } from './ApiContext';
import { useNotification } from './NotificationContext';
import { useHeartbeat } from '../hooks/useHeartbeat';
import logger from '../utils/logger';

// Read the persisted heartbeat sessionId for an agent (same key used in useHeartbeat)
const readLocalHeartbeatSessionId = (agentId) => {
  if (!agentId || typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(`cti_heartbeat_session_${agentId}`);
  } catch {
    return null;
  }
};

const MANUAL_PAUSE_STATUSES = new Set([
  'PAUSE',
  'ABSENT',
  'BREAK',
  'BATHROOM',
  'LUNCH',
  'REST',
  'TRAINING'
]);

const readStoredManualPauseStatus = (agentId) => {
  if (!agentId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`cti_agent_pause_status_${agentId}`);
    const normalized = raw ? String(raw).toUpperCase() : null;
    return normalized && MANUAL_PAUSE_STATUSES.has(normalized) ? normalized : null;
  } catch {
    return null;
  }
};

const AgentContext = createContext();

export const AgentProvider = ({ children }) => {
  const auth = useAuth();
  const api = useApi();
  const { showSuccess, showError, showInfo, showWarning } = useNotification();
  const [currentAgent, setCurrentAgent] = useState(null);
  const [agentStatus, setAgentStatus] = useState('NOTREADY');
  const [loading, setLoading] = useState(false);
  const [previewSession, setPreviewSession] = useState(null);
  const [previewStatus, setPreviewStatus] = useState('IDLE');
  const [previewCallInfo, setPreviewCallInfo] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewActionResponse, setPreviewActionResponse] = useState(null);

  const [preSessionConflict, setPreSessionConflict] = useState(false);
  const [preSessionInfo, setPreSessionInfo] = useState(null);
  const pendingAgentRef = useRef(null);
  const lastLocalStatusChangeRef = useRef(0); // timestamp del último cambio local de estado

  const resetPreviewState = useCallback(() => {
    setPreviewSession(null);
    setPreviewCallInfo(null);
    setPreviewStatus('IDLE');
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const trace = {
        loadedAt: new Date().toISOString()
      };
      window.__AGENT_CONTEXT_TRACE = trace;
      console.warn('[FRONTEND TRACE] AgentContext mounted', trace);
    }
  }, []);

  const handlePreviewData = useCallback((payload = {}) => {
    const delivery = payload?.__delivery || 'WS';
    logger.log('[PREVIEW] Data received:', payload);
    const tracePayload = {
      delivery,
      previewId: payload?.previewId,
      agentId: payload?.agentId,
      skill: payload?.skill,
      hasCustomerData: !!payload?.customerData
    };
    if (typeof window !== 'undefined') {
      window.__LAST_PREVIEW_TRACE = {
        type: 'PREVIEW_DATA',
        ...tracePayload,
        timestamp: new Date().toISOString()
      };
    }
    console.warn('[PREVIEW TRACE] PREVIEW_DATA received', tracePayload);
    setPreviewSession(payload);
    setPreviewStatus('REVIEWING');
    setPreviewCallInfo(null);
    setPreviewResult(null);
    setPreviewActionResponse(null);
  }, []);

  const handlePreviewCallInitiated = useCallback((payload = {}) => {
    logger.log('[PREVIEW] Call initiated:', payload);
    setPreviewStatus('CALLING');
    setPreviewCallInfo(payload);
  }, []);

  const handlePreviewCallEnd = useCallback((payload = {}) => {
    logger.log('[PREVIEW] Call ended:', payload);
    setPreviewResult(payload);
    resetPreviewState();
  }, [resetPreviewState]);

  const handlePreviewTimeout = useCallback((payload = {}) => {
    logger.warn('[PREVIEW] Preview timeout:', payload);
    setPreviewResult({ ...payload, reason: payload.reason || 'TIMEOUT' });
    resetPreviewState();
  }, [resetPreviewState]);

  const handlePreviewCancelled = useCallback((payload = {}) => {
    logger.warn('[PREVIEW] Preview cancelled:', payload);
    setPreviewResult({ ...payload, reason: payload.reason || 'CANCELLED' });
    resetPreviewState();
  }, [resetPreviewState]);

  const handlePreviewActionResponse = useCallback((payload = {}) => {
    setPreviewActionResponse({
      receivedAt: new Date().toISOString(),
      payload
    });
    if (payload?.success === false) {
      console.error('[PREVIEW] Action rejected:', payload);
    }
  }, []);

  // Initialize heartbeat for current agent
  const conflictPayloadRef = useRef(null);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const isLoggingOutRef = useRef(false); // 🔒 Flag para prevenir auto-reconexión durante logout

  const triggerSessionConflict = useCallback((payload = {}) => {
    conflictPayloadRef.current = payload;
    setSessionConflict(true);
  }, []);

  const triggerSessionTimeout = useCallback((payload = {}) => {
    logger.warn('[AGENT] Session timeout triggered:', payload);
    conflictPayloadRef.current = payload;
    setSessionTimeout(true);
    // Don't logout heartbeat automatically, let user decide
  }, []);

  // 🔒 NUEVO: Manejar sesión inválida desde el backend
  const handleSessionInvalid = useCallback((payload = {}) => {
    console.error('[AGENT] 🛡️ Session invalid received from backend:', payload);

    // Activar conflicto de sesión con información más específica
    triggerSessionConflict({
      ...payload,
      reason: 'session_invalid',
      message: payload.message || 'Tu sesión ya no es válida',
      timestamp: payload.timestamp || new Date().toISOString()
    });
  }, [triggerSessionConflict]);

  // Manejar timeout de heartbeat directamente del websocket
  const handleHeartbeatTimeout = useCallback((payload = {}) => {
    logger.warn('[AGENT] ⏰ Heartbeat timeout received from websocket:', payload);

    triggerSessionTimeout({
      ...payload,
      reason: 'heartbeat_timeout',
      message: 'Tu sesión se cerró por inactividad',
      timestamp: payload.timestamp || new Date().toISOString()
    });
  }, [triggerSessionTimeout]);

  // Manejar desconexión del agente por timeout
  const handleAgentTimeout = useCallback((payload = {}) => {
    logger.warn('[AGENT] 👤 Agent timeout received from websocket:', payload);

    triggerSessionTimeout({
      ...payload,
      reason: 'heartbeat_timeout',
      message: 'Tu sesión se cerró por inactividad',
      timestamp: payload.timestamp || new Date().toISOString()
    });
  }, [triggerSessionTimeout]);

  const previewEventHandlers = useMemo(() => ({
    PREVIEW_DATA: handlePreviewData,
    PREVIEW_CALL_INITIATED: handlePreviewCallInitiated,
    PREVIEW_CALL_END: handlePreviewCallEnd,
    PREVIEW_TIMEOUT: handlePreviewTimeout,
    PREVIEW_CANCELLED: handlePreviewCancelled,
    'preview:action:response': handlePreviewActionResponse,
    'session:invalid': handleSessionInvalid, // 🔒 NUEVO: Handler para sesión inválida
    'heartbeat:timeout': handleHeartbeatTimeout, // 🔒 NUEVO: Handler para timeout de heartbeat
    'agent:timeout': handleAgentTimeout, // 🔒 NUEVO: Handler para timeout del agente
    'agent:disconnected': (payload) => {
      if (payload.reason === 'heartbeat_timeout') {
        handleAgentTimeout(payload);
      }
    },
    'workflow_open_url': (payload) => {
      if (payload?.url) {
        if (payload.newTab) {
          window.open(payload.url, '_blank');
        } else {
          window.open(payload.url, '_blank');
        }
      }
    }
  }), [
    handlePreviewData,
    handlePreviewCallInitiated,
    handlePreviewCallEnd,
    handlePreviewTimeout,
    handlePreviewCancelled,
    handlePreviewActionResponse,
    handleSessionInvalid,
    handleHeartbeatTimeout,
    handleAgentTimeout
  ]);

  const heartbeat = useHeartbeat(api, currentAgent?.agent, {
    enabled: !!currentAgent?.agent,
    heartbeatInterval: 10000, // 10 seconds
    customEvents: previewEventHandlers
  });

  // Update triggerSessionConflict to include heartbeat logout
  const triggerSessionConflictWithLogout = useCallback((payload = {}) => {
    const reason = payload.reason || 'session_conflict';
    const normalizedPayload = { ...payload, reason };
    triggerSessionConflict(normalizedPayload);

    const allowedLogoutReasons = ['duplicate_session', 'session_replaced', 'supervisor_force', 'logout'];
    if (allowedLogoutReasons.includes(reason)) {
      heartbeat.logout();
    } else {
      logger.warn('[AGENT] Session conflict no-forced-logout (reason not allowed):', reason);
    }
  }, [triggerSessionConflict, heartbeat]);
  const heartbeatSocket = heartbeat.socket;

  useEffect(() => {
    if (!heartbeatSocket) return;

    const handleSessionReplaced = (payload = {}) => {
      const normalizedPayload = { ...payload, reason: payload.reason || 'session_replaced' };
      logger.warn('[AGENT] session:replaced received:', normalizedPayload);
      triggerSessionConflictWithLogout(normalizedPayload);
    };

    const handleSessionDisabled = (payload = {}) => {
      const normalizedPayload = { ...payload, reason: payload.reason || 'session_disabled' };
      const allowedReasons = ['duplicate_session', 'session_replaced', 'supervisor_force', 'logout'];

      logger.warn('[AGENT] session:disabled received:', normalizedPayload);

      if (allowedReasons.includes(normalizedPayload.reason)) {
        triggerSessionConflictWithLogout(normalizedPayload);
      } else {
        logger.warn('[AGENT] Ignorando session:disabled (reason no permitido para logout):', normalizedPayload.reason);
      }
    };

    heartbeatSocket.on('session:replaced', handleSessionReplaced);
    heartbeatSocket.on('session:disabled', handleSessionDisabled);

    return () => {
      heartbeatSocket.off('session:replaced', handleSessionReplaced);
      heartbeatSocket.off('session:disabled', handleSessionDisabled);
    };
  }, [heartbeatSocket, heartbeat, triggerSessionConflictWithLogout]);

  const sendPreviewAction = useCallback((action, payload = {}) => {
    if (!action) {
      logger.warn('[PREVIEW] Action is required to emit preview command');
      return false;
    }

    const normalizedAction = String(action).toUpperCase();
    const sanitizedPayload = payload || {};
    const activeSession = sanitizedPayload.previewSession || previewSession;
    const overridePreviewId = sanitizedPayload.previewId;
    const overrideSkill = sanitizedPayload.skill;
    const extra = sanitizedPayload.extra || {};
    const { previewSession: _ignoredSession, previewId: _ignoredPreview, skill: _ignoredSkill, extra: _ignoredExtra, ...rest } = sanitizedPayload;

    const targetPreviewId = overridePreviewId || activeSession?.previewId;
    const targetSkill = overrideSkill || activeSession?.skill;

    if (!targetSkill) {
      logger.warn('[PREVIEW] Cannot emit action without skill context', normalizedAction);
      return false;
    }

    const emitted = heartbeat.emitRealtimeEvent('preview:action', {
      action: normalizedAction,
      previewId: targetPreviewId,
      skill: targetSkill,
      extra: { ...extra, ...rest }
    });

    if (!emitted) {
      logger.warn('[PREVIEW] Failed to emit preview action', normalizedAction);
      return false;
    }

    if (normalizedAction === 'DIAL') {
      setPreviewStatus('DIALING');
    }

    if (normalizedAction === 'SKIP' || normalizedAction === 'REJECT') {
      setPreviewResult({ action: normalizedAction, previewId: targetPreviewId });
      resetPreviewState();
    }

    return true;
  }, [heartbeat, previewSession, resetPreviewState]);

  const checkExistingAgentSession = useCallback(async (agentId) => {
    if (!agentId || !api?.heartbeat?.getStatus) {
      return null;
    }

    const localSessionId = readLocalHeartbeatSessionId(agentId);

    try {
      const statusResp = await api.heartbeat.getStatus(agentId);
      const statusData = statusResp?.data || statusResp;
      if (!statusData) return null;

      const thresholdMs = Number(statusData.threshold ?? statusData.timeoutThreshold ?? 20000);
      const lastHeartbeat = statusData.lastHeartbeat ? new Date(statusData.lastHeartbeat) : null;
      const elapsedMs = lastHeartbeat ? (Date.now() - lastHeartbeat.getTime()) : statusData.timeSinceLastHeartbeat;
      const isConnected = statusData.connected || statusData.connectionState === 'connected';
      const withinThreshold = thresholdMs > 0 ? elapsedMs <= thresholdMs : true;
      const activeSessionId = statusData.sessionId || statusData.session_id || null;
      const isSameSession = Boolean(localSessionId && activeSessionId && localSessionId === activeSessionId);

      if (isConnected && withinThreshold) {
        return {
          ...statusData,
          lastHeartbeat,
          timeSinceLastHeartbeat: elapsedMs,
          threshold: thresholdMs,
          isSameSession,
          localSessionId
        };
      }
    } catch (error) {
      console.error('[AGENT] Error checking existing agent session:', error);
    }

    return null;
  }, [api?.heartbeat]);

  const waitForSessionInactive = useCallback(async (agentId, attempts = 5, delayMs = 700) => {
    for (let i = 0; i < attempts; i += 1) {
      const active = await checkExistingAgentSession(agentId);
      if (!active) {
        return true;
      }
      logger.log('[AGENT][PRESESSION] Still active, retrying check', { attempt: i + 1, attempts });
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    return false;
  }, [checkExistingAgentSession]);

  const performAgentLogin = useCallback(async (agentData) => {
    logger.log('[AGENT] performAgentLogin start', agentData);
    logger.time('[AGENT] performAgentLogin');
    // Set current agent
    setCurrentAgent(agentData);
    const restoredPause = readStoredManualPauseStatus(agentData.agent);
    const initialStatus = restoredPause || 'NOTREADY';
    setAgentStatus(initialStatus);
    resetPreviewState();
    setPreviewResult(null);
    setPreviewActionResponse(null);

    logger.log(`[AGENT] Agent ${agentData.agent} logged in (initial status: ${initialStatus})`);

    // Notify backend about the initial status
    try {
      const response = await api.agents.updateStatus(agentData.agent, {
        status: initialStatus,
        pause_code: ''
      });

      if (response.success) {
        logger.log(`[AGENT] Initial status ${initialStatus} set in backend for agent ${agentData.agent}`);
      } else {
        logger.warn(`[AGENT] Failed to set initial status in backend:`, response.message);
      }
    } catch (statusError) {
      console.error('[AGENT] Error setting initial status in backend:', statusError);
      // Don't fail the login just because status update failed
    }

    logger.timeEnd('[AGENT] performAgentLogin');
    return true;
  }, [api.agents, resetPreviewState]);

  /**
   * Login an agent and start heartbeat monitoring
   */
  const loginAgent = useCallback(async (agentData, options = {}) => {
    const { skipPrecheck = false } = options;
    try {
      logger.log('[AGENT] loginAgent start', { agentData, skipPrecheck });
      setLoading(true);

      if (!skipPrecheck) {
        const activeSession = await checkExistingAgentSession(agentData.agent);
        if (activeSession) {
          if (activeSession.isSameSession) {
            logger.log('[AGENT][PRESESSION] Active session matches local sessionId, allowing seamless reconnect');
          } else {
            logger.log('[AGENT][PRESESSION] Active session detected before login', activeSession);
            pendingAgentRef.current = agentData;
            setPreSessionInfo(activeSession);
            setPreSessionConflict(true);
            setSessionConflict(false);
            conflictPayloadRef.current = null;
            logger.warn('[AGENT] Existing session detected, showing pre-connection modal');
            return false;
          }
        }
      }

      const success = await performAgentLogin(agentData);
      return success;
    } catch (error) {
      console.error('[AGENT] Error during login:', error);
      return false;
    } finally {
      logger.log('[AGENT] loginAgent end');
      setLoading(false);
    }
  }, [checkExistingAgentSession, performAgentLogin]);

  const cancelPreSessionLogin = useCallback(async () => {
    setPreSessionConflict(false);
    setPreSessionInfo(null);
    pendingAgentRef.current = null;
    setSessionConflict(false);
    conflictPayloadRef.current = null;
    const hardRedirect = () => {
      try {
        localStorage.removeItem('cti_admin_token');
        localStorage.removeItem('cti_admin_user');
        sessionStorage.clear();
        document.cookie.split(';').forEach((c) => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
        });
      } catch (err) {
        logger.warn('[AGENT][PRESESSION] Error during local cleanup', err);
      }
      logger.log('[AGENT][PRESESSION] Hard redirect to /login (cancel)');
      window.location.href = '/login';
      setTimeout(() => window.location.reload(), 200);
    };
    try {
      logger.log('[AGENT][PRESESSION] Cancel -> logout + redirect (fire-and-forget)');
      // No esperar logout para no bloquear el redirect
      auth.logout?.().catch(() => {});
    } finally {
      hardRedirect();
    }
  }, [auth]);

  const forcePreSessionLogin = useCallback(async () => {
    const agentData = pendingAgentRef.current;
    if (!agentData) {
      logger.warn('[AGENT] No pending agent to force login');
      return false;
    }

    try {
      setLoading(true);
      logger.time('[AGENT][PRESESSION] forceLogin total');
      const statusBefore = preSessionInfo || await checkExistingAgentSession(agentData.agent);
      const sessionId = statusBefore?.sessionId || null;

      try {
        if (sessionId) {
          logger.log('[AGENT][PRESESSION] Calling heartbeat.logout for session', sessionId);
          await api.heartbeat.logout(agentData.agent, sessionId);
        }
      } catch (err) {
        logger.warn('[AGENT] heartbeat logout failed', err);
      }

      try {
        logger.log('[AGENT][PRESESSION] Calling agents.forceDisconnect');
        await api.agents.forceDisconnect(agentData.agent);
      } catch (err) {
        logger.warn('[AGENT] forceDisconnect failed', err);
      }

      logger.log('[AGENT][PRESESSION] Waiting for session to clear...');
      const cleared = await waitForSessionInactive(agentData.agent, 6, 700);

      if (!cleared) {
        console.error('[AGENT] No se pudo limpiar la sesión previa del agente');
        logger.timeEnd('[AGENT][PRESESSION] forceLogin total');
        return false;
      }

      heartbeat.clearSessionId?.();
      heartbeat.forceNewSessionId?.();

      setPreSessionConflict(false);
      setPreSessionInfo(null);
      setSessionConflict(false);
      conflictPayloadRef.current = null;
      logger.log('[AGENT][PRESESSION] Performing agent login after force clear');
      const result = await performAgentLogin(agentData);
      pendingAgentRef.current = null;
      logger.timeEnd('[AGENT][PRESESSION] forceLogin total');
      return result;
    } catch (error) {
      console.error('[AGENT] Error forcing pre-session login:', error);
      logger.timeEnd('[AGENT][PRESESSION] forceLogin total');
      return false;
    } finally {
      setLoading(false);
    }
  }, [api.agents, api.heartbeat, checkExistingAgentSession, heartbeat, performAgentLogin, preSessionInfo, waitForSessionInactive]);

  /**
   * Logout current agent and stop heartbeat
   */
  const logoutAgent = useCallback(async () => {
    try {
      if (!currentAgent) return true;

      setLoading(true);
      isLoggingOutRef.current = true; // 🔒 Activar flag de logout

      logger.log(`[AGENT] Logging out agent ${currentAgent.agent}`);

      // Send logout to backend
      await heartbeat.logout();

      // Clear current agent
      setCurrentAgent(null);
      setAgentStatus('OFFLINE');
      resetPreviewState();
      setPreviewResult(null);
      setPreviewActionResponse(null);

      logger.log('[AGENT] Agent logged out successfully');

      return true;
    } catch (error) {
      console.error('[AGENT] Error during logout:', error);
      return false;
    } finally {
      setLoading(false);
      // Mantener la flag activa para evitar reconexión automática
    }
  }, [currentAgent, heartbeat, resetPreviewState]);

  /**
   * Change agent status
   */
  const changeAgentStatus = useCallback(async (newStatus, pauseCode = '', options = {}) => {
    const {
      skipBackend = false,
      silent = true,
      expectedStatuses = null,
      reason: changeReason = '',
      metadata = null
    } = options;

    if (!currentAgent) {
      logger.warn('[AGENT] No current agent to change status');
      return false;
    }

    if (!newStatus) {
      logger.warn('[AGENT] Missing status value for change');
      return false;
    }

    if (skipBackend) {
      logger.log(`[AGENT] Applying local status change to ${newStatus} (skipBackend=true)`);
      setAgentStatus(newStatus);
      if (!silent) {
        showInfo(`Tu estado cambió a ${newStatus}`);
      }
      return true;
    }

    setLoading(true);
    logger.log(`[AGENT] Changing status for agent ${currentAgent.agent} to ${newStatus}`);

    try {
      const payload = {
        status: newStatus,
        pause_code: pauseCode
      };

      const normalizedExpected = (() => {
        if (!expectedStatuses) return null;
        if (Array.isArray(expectedStatuses)) {
          return expectedStatuses.filter(Boolean).map(value => typeof value === 'string' ? value.toUpperCase() : value);
        }
        return [String(expectedStatuses).toUpperCase()];
      })();

      if (normalizedExpected && normalizedExpected.length > 0) {
        payload.expected_statuses = normalizedExpected;
      }

      if (changeReason) {
        payload.reason = changeReason;
      }

      if (metadata && Object.keys(metadata).length > 0) {
        payload.metadata = metadata;
      }

      const response = await api.agents.updateStatus(currentAgent.agent, payload);

      if (response.success) {
        const resolvedStatus = response.status || newStatus;
        lastLocalStatusChangeRef.current = Date.now();
        setAgentStatus(resolvedStatus);
        logger.log(`[AGENT] Status changed successfully to ${resolvedStatus}`);
        if (!silent) {
          showSuccess(`Estado cambiado a ${resolvedStatus}`);
        }
        return true;
      }

      if (response.skipped && response.code === 'STATUS_OUT_OF_SEQUENCE') {
        logger.warn('[AGENT] Status change skipped (out-of-sequence):', {
          requested: newStatus,
          current: response.status || response.currentStatus
        });
        if (response.status || response.currentStatus) {
          setAgentStatus(response.status || response.currentStatus);
        }
        return true;
      }

      console.error('[AGENT] Failed to change status:', response.message);
      if (!silent) {
        showError(response.message || 'Error cambiando estado');
      }
      return false;
    } catch (error) {
      console.error('[AGENT] Error changing status:', error);
      if (!silent) {
        showError('No se pudo cambiar el estado');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentAgent, api.agents, showError, showInfo, showSuccess]);

  /**
   * Sync agent status with backend
   */
  const syncAgentStatus = useCallback(async () => {
    try {
      if (!currentAgent) return null;

      const response = await api.heartbeat.syncStatus(currentAgent.agent);

      if (response?.success && response.data?.status) {
        setAgentStatus(response.data.status);
        logger.log(`[AGENT] Status synced: ${response.data.status}`);
        return response.data.status;
      }

      return null;
    } catch (error) {
      console.error('[AGENT] Error syncing status:', error);
      return null;
    }
  }, [currentAgent, api.heartbeat]);

  useEffect(() => {
    if (!heartbeatSocket || !currentAgent?.agent) {
      logger.log('[AGENT] Skipping supervisor event setup - socket:', !!heartbeatSocket, 'agent:', currentAgent?.agent);
      return;
    }

    logger.log('[AGENT] Setting up supervisor event listeners for agent:', currentAgent.agent);

    const handleSupervisorStatusChange = async (payload = {}) => {
      const targetId = payload.agent || payload.agentId || payload.agent_id;
      if (targetId && targetId !== currentAgent.agent) {
        return;
      }

      const nextStatus = payload.status;
      if (!nextStatus) {
        logger.warn('[AGENT] supervisor:status-change sin status recibido', payload);
        return;
      }

      const pauseCode = payload.pauseCode ?? payload.pause_code ?? '';
      logger.warn(`[AGENT] Supervisor requested status change to ${nextStatus}`, payload);
      await changeAgentStatus(nextStatus, pauseCode, { skipBackend: true, silent: true });
      await syncAgentStatus();
    };

    const handleSupervisorForceDisconnect = async (payload = {}) => {
      logger.log('[AGENT] supervisor:force-disconnect event received, payload:', payload);
      logger.log('[AGENT] Current agent:', currentAgent?.agent);

      const targetId = payload.agent || payload.agentId || payload.agent_id;
      logger.log('[AGENT] Target ID from payload:', targetId);

      if (targetId && targetId !== currentAgent.agent) {
        logger.log('[AGENT] Event not for this agent, ignoring');
        return;
      }

      logger.warn('[AGENT] supervisor:force-disconnect PROCESSING for agent', currentAgent.agent);
      showWarning('Tu sesión fue cerrada por un supervisor');

      try {
        // 1. Primero logout del agente
        logger.log('[SUPERVISOR DISCONNECT] 1 Cerrando sesión del agente...');
        await logoutAgent();

        // 2. Luego logout de autenticación (auth context)
        logger.log('[SUPERVISOR DISCONNECT] 2 Cerrando sesión de autenticación...');
        if (auth && auth.logout) {
          await auth.logout().catch(error => {
            logger.warn('[SUPERVISOR DISCONNECT] Warning en logout de auth:', error);
          });
        }

        // 3. Forzar redirección al login
        logger.log('[SUPERVISOR DISCONNECT] 3 Redirigiendo a login...');
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);

      } catch (error) {
        console.error('[SUPERVISOR DISCONNECT] Error durante desconexión forzada:', error);
        // En caso de error, igual forzar redirección
        window.location.href = '/login';
      }
    };

    heartbeatSocket.on('supervisor:status-change', handleSupervisorStatusChange);
    heartbeatSocket.on('supervisor:force-disconnect', handleSupervisorForceDisconnect);

    return () => {
      heartbeatSocket.off('supervisor:status-change', handleSupervisorStatusChange);
      heartbeatSocket.off('supervisor:force-disconnect', handleSupervisorForceDisconnect);
    };
  }, [heartbeatSocket, currentAgent?.agent, changeAgentStatus, syncAgentStatus, logoutAgent, showInfo, showWarning, auth]);

  // Set up SSE listeners for agent events
  useEffect(() => {
    if (!api.sse || !currentAgent) return;

    const handleAgentUpdate = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.agent?.agent === currentAgent.agent) {
          logger.log('[AGENT] Received agent update via SSE:', data);

          if (data.agent?.status) {
            // Ignorar SSE updates que llegan dentro de 3s de un cambio local
            // para evitar que un evento rezagado sobrescriba el estado recién cambiado
            const msSinceLocalChange = Date.now() - lastLocalStatusChangeRef.current;
            if (msSinceLocalChange < 3000) {
              logger.log(`[AGENT] SSE status update ignored (local change was ${msSinceLocalChange}ms ago):`, {
                sseStatus: data.agent.status,
                currentStatus: agentStatus
              });
            } else {
              setAgentStatus(data.agent.status);
            }
          }

          // Detectar desconexión forzada por supervisor
          if (
            data.type === 'disconnected' &&
            data.agent?.reason === 'supervisor_force'
          ) {
            logger.warn('[AGENT] Desconexión forzada por supervisor detectada via SSE agent-update');
            showWarning('Tu sesión fue cerrada por un supervisor');

            // Ejecutar el mismo proceso de logout que usa el menú
            logger.log('[SUPERVISOR DISCONNECT]  Iniciando logout forzado por supervisor...');

            // 1. PRIMERO: Destruir token inmediatamente para cortar autenticación
            logger.log('[SUPERVISOR DISCONNECT] 1 Destruyendo token de autenticación...');
            localStorage.removeItem('cti_admin_token');
            localStorage.removeItem('cti_admin_user');
            sessionStorage.clear();

            // Limpiar cookies inmediatamente
            document.cookie.split(';').forEach((c) => {
              document.cookie = c
                .replace(/^ +/, '')
                .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
            });

            // 2. Intentar logout del agente en background (sin esperar)
            logger.log('[SUPERVISOR DISCONNECT] 2 Iniciando logout de agente en background...');
            logoutAgent().catch(error => {
              logger.warn('[SUPERVISOR DISCONNECT] Warning en logout de agente:', error);
            });

            // 3. Llamar al logout de auth (sin esperar respuesta)
            logger.log('[SUPERVISOR DISCONNECT] 3 Llamando logout de auth en background...');
            if (auth && auth.logout) {
              auth.logout().catch(error => {
                logger.warn('[SUPERVISOR DISCONNECT] Warning en logout de auth:', error);
              });
            }

            // 4. INMEDIATAMENTE: Forzar recarga de la página para ir al login
            logger.log('[SUPERVISOR DISCONNECT] 4 Forzando redirección a login...');

            // Usar un timeout muy pequeño para permitir que los logs se vean
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);

            return; // No procesar más
          }

          // Detectar conflicto de sesión duplicada
          if (
            data.type === 'disconnected' &&
            data.agent?.reason === 'duplicate_session' &&
            data.agent?.sessionId &&
            heartbeat.getSessionId?.() === data.agent.sessionId
          ) {
            triggerSessionConflictWithLogout({
              reason: data.agent.reason,
              timestamp: data.timestamp,
              agentId: data.agent.agent
            });
          }
        }
      } catch (error) {
        console.error('[AGENT] Error parsing agent update SSE:', error);
      }
    };

    const handleAgentConnection = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.agent === currentAgent.agent) {
          logger.log('[AGENT] Received agent connection event via SSE:', data);

          if (data.type === 'connected') {
            logger.log(`[AGENT] Agent ${currentAgent.agent} connected`);
            showSuccess(`Agente ${currentAgent.agent} conectado`);
          } else if (data.type === 'disconnected') {
            logger.log(`[AGENT] Agent ${currentAgent.agent} disconnected: ${data.reason}`);

            if (data.reason === 'supervisor_force') {
              // Agent was disconnected by supervisor - force logout immediately
              logger.warn('[AGENT] Desconexión forzada por supervisor detectada via SSE');
              showWarning('Tu sesión fue cerrada por un supervisor');

              // Ejecutar el mismo proceso de logout que usa el menú
              logger.log('[SUPERVISOR DISCONNECT]  Iniciando logout forzado por supervisor...');

              // 1. PRIMERO: Destruir token inmediatamente para cortar autenticación
              logger.log('[SUPERVISOR DISCONNECT] 1 Destruyendo token de autenticación...');
              localStorage.removeItem('cti_admin_token');
              localStorage.removeItem('cti_admin_user');
              sessionStorage.clear();

              // Limpiar cookies inmediatamente
              document.cookie.split(';').forEach((c) => {
                document.cookie = c
                  .replace(/^ +/, '')
                  .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
              });

              // 2. Intentar logout del agente en background (sin esperar)
              logger.log('[SUPERVISOR DISCONNECT] 2 Iniciando logout de agente en background...');
              logoutAgent().catch(error => {
                logger.warn('[SUPERVISOR DISCONNECT] Warning en logout de agente:', error);
              });

              // 3. Llamar al logout de auth (sin esperar respuesta)
              logger.log('[SUPERVISOR DISCONNECT] 3 Llamando logout de auth en background...');
              if (auth && auth.logout) {
                auth.logout().catch(error => {
                  logger.warn('[SUPERVISOR DISCONNECT] Warning en logout de auth:', error);
                });
              }

              // 4. INMEDIATAMENTE: Forzar recarga de la página para ir al login
              logger.log('[SUPERVISOR DISCONNECT] 4 Forzando redirección a login...');

              // Usar un timeout muy pequeño para permitir que los logs se vean
              setTimeout(() => {
                window.location.href = '/login';
              }, 100);

            } else if (data.reason === 'heartbeat_timeout') {
              // Agent was disconnected due to heartbeat timeout
              setAgentStatus('OFFLINE');
              triggerSessionTimeout({
                reason: 'heartbeat_timeout',
                timestamp: data.timestamp,
                agentId: data.agent || currentAgent.agent,
                message: 'Tu sesión se cerró por inactividad'
              });
            } else {
              showInfo(`Agente ${currentAgent.agent} desconectado: ${data.reason}`);
            }
          }
        }
      } catch (error) {
        console.error('[AGENT] Error parsing agent connection SSE:', error);
      }
    };

    const handlePreviewSseEvent = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data?.preview) return;

        const payloadAgentId = data.agentId != null ? String(data.agentId).trim() : '';
        const currentAgentId = currentAgent?.agent != null ? String(currentAgent.agent).trim() : '';

        if (payloadAgentId && currentAgentId && payloadAgentId !== currentAgentId) {
          console.warn('[PREVIEW TRACE] preview-data ignored by agent mismatch', {
            payloadAgentId,
            currentAgentId,
            previewId: data?.preview?.previewId
          });
          return;
        }

        const previewPayload = data.preview;
        const currentPreviewId = previewSession?.previewId;

        if (previewPayload?.previewId && previewPayload.previewId === currentPreviewId) {
          return;
        }

        logger.log('[AGENT] Received preview payload via SSE fallback:', previewPayload);
        const sseTrace = {
          previewId: previewPayload?.previewId,
          agentId: data?.agentId,
          skill: previewPayload?.skill
        };
        if (typeof window !== 'undefined') {
          window.__LAST_PREVIEW_TRACE = {
            type: 'SSE_PREVIEW_EVENT',
            ...sseTrace,
            timestamp: new Date().toISOString()
          };
        }
        console.warn('[PREVIEW TRACE] preview-data SSE event', sseTrace);
        handlePreviewData({
          ...previewPayload,
          __delivery: 'SSE'
        });
      } catch (error) {
        console.error('[AGENT] Error parsing preview SSE event:', error);
      }
    };

    // Add event listeners
    api.sse.addEventListener('agent-update', handleAgentUpdate);
    api.sse.addEventListener('agent-connected', handleAgentConnection);
    api.sse.addEventListener('agent-disconnected', handleAgentConnection);
    api.sse.addEventListener('preview-data', handlePreviewSseEvent);

    return () => {
      // Remove event listeners
      if (api.sse) {
        api.sse.removeEventListener('agent-update', handleAgentUpdate);
        api.sse.removeEventListener('agent-connected', handleAgentConnection);
        api.sse.removeEventListener('agent-disconnected', handleAgentConnection);
        api.sse.removeEventListener('preview-data', handlePreviewSseEvent);
      }
    };
  }, [api.sse, currentAgent, handlePreviewData, previewSession?.previewId, triggerSessionConflictWithLogout, heartbeat, logoutAgent, auth, showWarning, showSuccess, showInfo, setAgentStatus, triggerSessionTimeout]);

  // Auto-sync status periodically
  useEffect(() => {
    if (!currentAgent) return;

    const syncInterval = setInterval(syncAgentStatus, 60000); // Sync every minute

    return () => {
      clearInterval(syncInterval);
    };
  }, [currentAgent, syncAgentStatus]);

  // Auto-login agent and set initial status when user is authenticated
  useEffect(() => {
    logger.log('[AGENT] useEffect triggered - checking auth state');
    logger.log('[AGENT] Auth state:', {
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      user: auth.user,
      currentAgent: currentAgent
    });

    const autoSetInitialStatus = async () => {
      logger.log('[AGENT] autoSetInitialStatus function called');

      // 🔒 NUEVO: Verificar si estamos en proceso de logout
      if (isLoggingOutRef.current) {
        logger.log('[AGENT]  Logout in progress - skipping auto-status');
        return;
      }

      // Only run if:
      // 1. User is authenticated
      // 2. No agent is currently logged in
      // 3. Auth loading is complete
      if (!auth.isAuthenticated) {
        logger.log('[AGENT] User not authenticated - skipping auto-status');
        // Limpiar flag de logout cuando no hay autenticación
        isLoggingOutRef.current = false;
        return;
      }

      if (auth.isLoading) {
        logger.log('[AGENT] Auth still loading - skipping auto-status');
        return;
      }

      if (currentAgent) {
        logger.log('[AGENT] Agent already logged in - skipping auto-status', currentAgent);
        return;
      }

      logger.log('[AGENT] All conditions met - proceeding with auto-status');
      const user = auth.user;
      logger.log('[AGENT] User object:', user);

      // Check if the authenticated user is an agent (use username or id)
      const agentId = user.username || user.id;
      if (user && agentId) {
        logger.log(`[AGENT] Attempting agent login with pre-check for ${agentId} after page load`);

        const agentData = {
          agent: agentId,
          name: user.name || agentId,
          extension: user.extension || ''
        };

        await loginAgent(agentData);
      } else {
        logger.log('[AGENT] User is missing username/id or not authenticated properly:', user);
      }
    };

    autoSetInitialStatus();
  }, [auth.isAuthenticated, auth.isLoading, auth.user, currentAgent, loginAgent]);

  const value = {
    currentAgent,
    agentStatus,
    loading,
    loginAgent,
    logoutAgent,
    changeAgentStatus,
    syncAgentStatus,
    heartbeat,
    previewSession,
    previewStatus,
    previewCallInfo,
    previewResult,
    previewActionResponse,
    sendPreviewAction,
    resetPreviewState,
    preSessionConflict,
    preSessionInfo,
    forcePreSessionLogin,
    cancelPreSessionLogin,
    telephonyReady: !!currentAgent && !preSessionConflict,
    sessionConflict,
    sessionTimeout,
    conflictInfo: conflictPayloadRef.current,
    resolveSessionConflict: () => {
      setSessionConflict(false);
      conflictPayloadRef.current = null;
    },
    resolveSessionTimeout: () => {
      setSessionTimeout(false);
      conflictPayloadRef.current = null;
    },
    forceSessionReset: async () => {
      // 🔒 MEJORADO: Primero desconectar la sesión existente del backend, luego generar nueva
      try {
        // Logout la sesión actual del backend si existe
        if (heartbeat.logout) {
          await heartbeat.logout();
        }

        // Limpiar sessionId local
        heartbeat.clearSessionId?.();

        // Generar nuevo sessionId
        const newSessionId = heartbeat.forceNewSessionId?.();
        logger.log('[AGENT]  Session reset completed, new sessionId:', newSessionId);

        return newSessionId;
      } catch (error) {
        console.error('[AGENT] Error during force session reset:', error);
        // Fallback: just clear and generate new
        heartbeat.clearSessionId?.();
        return heartbeat.forceNewSessionId?.();
      }
    }
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};
